import { LocalStorage } from '@ng-idle/core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';

import { registerLocaleData } from '@angular/common';
import localePL from '@angular/common/locales/pl';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';

import { environment } from '@environment';
import { effects, facades, reducers } from '@state';
import { CustomSerializer } from '@state/router';
import * as authActions from '@state/user-auth/user-auth.actions';

import { CoreModule } from '@core/core.module';
import { AuthGuard } from '@core/guards/auth.guard';
import { DocumentsGuard } from '@core/guards/documents.guard';
import { LanguagesGuard } from '@core/guards/languages.guard';
import { ProcessesGuard } from '@core/guards/processes.guard';
import { RequiredDataGuard } from '@core/guards/required-data.guard';
import { VoivodeshipsGuard } from '@core/guards/voivodeships.guard';
import { IdleStorage } from '@core/idle-storage';

import { fullDateFormat } from '@constants';
import { LayoutComponent } from '@layout/layout.component';
import { CookieBannerComponent } from '@shared/cookie-banner/cookie-banner.component';
import { UeFinanceBannerComponent } from '@shared/ue-finance-banner/ue-finance-banner.component';
import { CustomMatPaginatorIntl } from '@shared/mat-paginator-intl';
import { SharedModule } from '@shared/shared.module';

import { AppInitService } from './app-init.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { ActivateAccountApiUnavailableViewComponent } from './activate-account/api-unavailable-view/activate-account-api-unavailable-view.component';
import { ActivateAccountComponent } from './activate-account/activate-account.component';
import { ActivateAccountFormStateComponent } from './activate-account/form-state/activate-account-form-state.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';

import { UserProcessFormComponent } from './user-process-form/user-process-form.component';
import { NoProcessesAvailableComponent } from './no-processes-available/no-processes-available.component';
import { UserSettingsComponent } from './user-settings/user-settings.component';

import { ApplicationSelectorComponent } from '@layout/application-selector/application-selector.component';
import { LoginApiUnavailableViewComponent } from './login/api-unavailable-view/login-api-unavailable-view.component';
import { SidebarMenuComponent } from '@layout/sidebar-menu/sidebar-menu.component';
import { LoginDesktopCredentialsStepComponent } from './login/desktop-view/credentials-step/login-desktop-credentials-step.component';
import { LoginDesktopMfaStepComponent } from './login/desktop-view/mfa-step/login-desktop-mfa-step.component';
import { LoginDesktopViewComponent } from './login/desktop-view/login-desktop-view.component';
import { LoginComponent } from './login/login.component';
import { LoginMobileCredentialsStepComponent } from './login/mobile-view/credentials-step/login-mobile-credentials-step.component';
import { LoginMobileMfaStepComponent } from './login/mobile-view/mfa-step/login-mobile-mfa-step.component';
import { LoginMobileViewComponent } from './login/mobile-view/login-mobile-view.component';
import { NotificationsComponent } from './notifications/notifications.component';

import { IsForeignerGuard } from '@core/guards/is-foreigner.guard';
import { ChatDesktopComponent } from './chat/chat-desktop/chat-desktop.component';
import { ChatMobileButtonComponent } from './chat/chat-mobile-button/chat-mobile-button.component';
import { ChatMobileComponent } from './chat/chat-mobile/chat-mobile.component';
import { NewAccountApiUnavailableViewComponent } from './new-account/api-unavailable-view/new-account-api-unavailable-view.component';
import { NewAccountDesktopFinishedStateComponent } from './new-account/desktop-view/finished-state/new-account-desktop-finished-state.component';
import { NewAccountDesktopFormStateComponent } from './new-account/desktop-view/form-state/new-account-desktop-form-state.component';
import { NewAccountDesktopInactiveStateComponent } from './new-account/desktop-view/inactive-state/new-account-desktop-inactive-state.component';
import { NewAccountDesktopViewComponent } from './new-account/desktop-view/new-account-desktop-view.component';
import { NewAccountMobileFinishedStateComponent } from './new-account/mobile-view/finished-state/new-account-mobile-finished-state.component';
import { NewAccountMobileFormStateComponent } from './new-account/mobile-view/form-state/new-account-mobile-form-state.component';
import { NewAccountMobileInactiveStateComponent } from './new-account/mobile-view/inactive-state/new-account-mobile-inactive-state.component';
import { NewAccountMobileViewComponent } from './new-account/mobile-view/new-account-mobile-view.component';
import { NewAccountComponent } from './new-account/new-account.component';
import { AccountDeletedComponent } from './account-deleted/account-deleted.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { TOSComponent } from './tos/tos.component';
import { TopbarMenuComponent } from '@layout/topbar-menu/topbar-menu.component';
import { UserSettingsDetailsComponent } from './user-settings/user-settings-details/user-settings-details.component';
import { UserSettingsTransactionsComponent } from './user-settings/user-settings-transactions/user-settings-transactions.component';
import { UserSettingsListOfRequestsComponent } from './user-settings/user-settings-list-of-requests/user-settings-list-of-requests.component';
import { UserSettingsConsentsComponent } from './user-settings/user-settings-consents/user-settings-consents.component';
import { UserSettingsModalsModule } from './user-settings/user-settings-modals.module';
import { InitUserComponent } from './init-user/init-user.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { FeatureModuleGuard } from '@core/guards/feature-module.guard';

const config: SocketIoConfig = {
  url: environment.SOCKET_URL,
  options: {
    // transports: ['polling'],
    // upgrade: false,
    transports: ['websocket'],
    withCredentials: true,
    autoConnect: false,
  },
};

registerLocaleData(localePL);

const components = [
  AppComponent,
  InitUserComponent,
  LoginApiUnavailableViewComponent,
  LoginComponent,
  LoginDesktopCredentialsStepComponent,
  LoginDesktopMfaStepComponent,
  LoginDesktopViewComponent,
  LoginMobileCredentialsStepComponent,
  LoginMobileMfaStepComponent,
  LoginMobileViewComponent,
  LayoutComponent,
  UserSettingsComponent,
  UserSettingsDetailsComponent,
  UserSettingsTransactionsComponent,
  UserSettingsListOfRequestsComponent,
  UserSettingsConsentsComponent,
  ActivateAccountApiUnavailableViewComponent,
  ActivateAccountComponent,
  ActivateAccountFormStateComponent,
  ResetPasswordComponent,
  UserProcessFormComponent,
  NoProcessesAvailableComponent,
  CookieBannerComponent,
  UeFinanceBannerComponent,
  NotificationsComponent,
  TopbarMenuComponent,
  ApplicationSelectorComponent,
  SidebarMenuComponent,
  ChatMobileComponent,
  ChatDesktopComponent,
  ChatMobileButtonComponent,
  NewAccountApiUnavailableViewComponent,
  NewAccountComponent,
  NewAccountDesktopFinishedStateComponent,
  NewAccountDesktopFormStateComponent,
  NewAccountDesktopInactiveStateComponent,
  NewAccountDesktopViewComponent,
  NewAccountMobileFinishedStateComponent,
  NewAccountMobileFormStateComponent,
  NewAccountMobileInactiveStateComponent,
  NewAccountMobileViewComponent,
  AccountDeletedComponent,
  PrivacyPolicyComponent,
  TOSComponent,
];

const guards = [
  AuthGuard,
  LanguagesGuard,
  VoivodeshipsGuard,
  RequiredDataGuard,
  DocumentsGuard,
  ProcessesGuard,
  IsForeignerGuard,
  FeatureModuleGuard,
];

export function clearState(reducer: any): any {
  return (state: any, action: any) => {
    if (action.type === authActions.logout.type) {
      state = undefined;
    }

    return reducer(state, action);
  };
}

export const MY_FORMATS = {
  parse: {
    dateInput: fullDateFormat,
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: fullDateFormat,
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const DIALOG_ANIMATION_DEFAULTS: Partial<MatDialogConfig> = {
  enterAnimationDuration: 230,
  exitAnimationDuration: 180,
  closeOnNavigation: true,
};

@NgModule({
  declarations: [...components],
  imports: [
    StoreModule.forRoot(reducers, { metaReducers: [clearState] }),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 25,
          autoPause: true,
          connectInZone: true,
        })
      : [],
    EffectsModule.forRoot(effects),
    StoreRouterConnectingModule.forRoot({ serializer: CustomSerializer }),
    SharedModule,
    UserSettingsModalsModule,
    CoreModule,
    AppRoutingModule,
    SocketIoModule.forRoot(config),
    NgIdleKeepaliveModule.forRoot(),
  ],
  providers: [
    ...guards,
    ...facades,
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitService: AppInitService) => () => appInitService.ensureLangsLoaded(),
      deps: [AppInitService],
      multi: true,
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: DIALOG_ANIMATION_DEFAULTS },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: LOCALE_ID, useValue: 'pl-PL' },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    { provide: LocalStorage, useClass: IdleStorage },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
